import React, { useState, useEffect } from 'react'
import './App.scss'
import { Provider } from 'react-redux'
import Login from './login'
import SignUp from './sign-up'
import Spinner from './spinner'
import api from './api-client'
import Page from './page'
import PublicPage from './public-page'
import Home from './home'
import CompanyLandingPage from './company-landing-page'
import { Redirect, Router, Switch, Route } from 'react-router-dom'
import history from './history'
import { useSelector } from 'react-redux'
import { setUser } from './redux/slices/user'
import store from './redux/store'
import UserSite from './user-site'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { REACT_APP_AUTH0_AUDIENCE, REACT_APP_AUTH0_CLIENT_ID, REACT_APP_AUTH0_DOMAIN, REACT_APP_ENV } from './config'
import * as Sentry from "@sentry/react"
import NotFound from './not-found'
import ErrorFallback from './error-fallback'
import NotAuthorized from './not-authorized'

if (REACT_APP_ENV !== 'local') {
  Sentry.init({
    dsn: 'https://a2c07ef08d1fe763784e782ee1806f1e@o4508212757463040.ingest.us.sentry.io/4510046603378688',
    environment: REACT_APP_ENV,
  })

  Sentry.setTag('app', 'admin')
}

const App = (props) => {
  const [loading, setLoading] = useState(true)

  const { user, hasToken } = useSelector(s => s.user)

  const { user: auth0User, isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    if (user) {
      if (user.isSuperUser && ['/', '/login'].includes(window.location.pathname)) {
        history.push('/moderate')
      } else if (!user.isSuperUser) {
        history.push('/home')
      }
    } else if (!user && hasToken) {
      api.getMe().then(me => {
        store.dispatch(setUser(me))
      }).catch(ex => {
        // if they previously had a token, assume that an error means they are logged out
        history.push('/login')
      }).finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [hasToken, user])

  useEffect(() => {
    const handleGetUser = async () => {
      const token = await getAccessTokenSilently()
      // token is from auth0 currently
      api.setToken(token)
      window.localStorage.setItem('_token', token)
      // get user with auth0 token
      api.getMe().then(me => {
        store.dispatch(setUser(me))
      }).catch(ex => {
        // if they previously had a token, assume that an error means they are logged out
        history.push('/login')
      }).finally(() => setLoading(false))
    }
    if (!isLoading && isAuthenticated && auth0User) {
      handleGetUser()
    }
  }, [auth0User, isAuthenticated, isLoading])

  if (loading || isLoading) {
    return <div className="container">
      <Spinner variant='block' />
    </div>
  }

  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} >
      <Router history={history}>
        <div>
          <Switch>
            <Route path="/" component={Home} exact />
            <Route path="/login" component={Login} />
            <Route path="/signup" component={SignUp} />
            <Route path="/forgot-password" component={PublicPage} />
            <Route path="/reset-password/:token" component={PublicPage} />
            {/* <Redirect from='/b/:slug/:abbreviation' to='/b/:slug'/> */}
            <Route path="/b/:slug" component={CompanyLandingPage} />
            {user ? (user.isSuperUser ? (
              <Route {...props} render={p => (
                <Page {...p} />
              )} />
            ) : (
              <Route {...props} render={p => (
                <NotAuthorized />
              )} />
              // <UserSite /> // renders the user site where API management is focus
            )) : <Redirect to={{
              pathname: "/login"
              // state: { from: location }
            }} />}
            <Route path="*" component={NotFound} />
          </Switch>
        </div>
      </Router>
    </Sentry.ErrorBoundary>
    
  )
}

const ConnectedApp = () => {
  return (
    <Provider store={store}>
      <Auth0Provider
        clientId={REACT_APP_AUTH0_CLIENT_ID}
        domain={REACT_APP_AUTH0_DOMAIN}
        authorizationParams={{
          audience: REACT_APP_AUTH0_AUDIENCE,
          redirect_uri: `${window.location.origin}/callback`,
        }}
      >
        <App />
      </Auth0Provider>
    </Provider>
  )
}

export default ConnectedApp
